import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useGarageWS } from "../hooks/useWebSocket";
import api from "../lib/api";
import { NotificationBell } from "../components/NotificationBell";
import { StatCard } from "../components/StatCard";
import { LogOut, Wrench, Search, Plus, Minus, ShoppingCart, IndianRupee, X, Receipt, Undo2, ChevronLeft, ChevronRight } from "lucide-react";
import MacJitLogo from "../components/MacJitLogo";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { isValidEmail, isValidIndianPhone, normalizeIndianPhone } from "../lib/validation";

const PAGE_SIZE = 12;

export default function ShopPage() {
  const { user, token, logout } = useAuth();
  const [inventory, setInventory] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [page, setPage] = useState(1);
  const [cart, setCart] = useState([]); // [{id, name, sku, price, qty, stock}]
  const [customer, setCustomer] = useState({ name: "", phone: "", email: "" });
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [sales, setSales] = useState([]);
  const [stats, setStats] = useState({});
  const [refunds, setRefunds] = useState([]);
  const [productBookings, setProductBookings] = useState([]);
  const [tick, setTick] = useState(0);
  const [lastSale, setLastSale] = useState(null);
  const [refundFor, setRefundFor] = useState(null); // sale obj or null
  const [refundReason, setRefundReason] = useState("");
  const [fittingCharge, setFittingCharge] = useState(0);
  const [manualAddOn, setManualAddOn] = useState({ label: "", amount: "" });
  const [billingSettings, setBillingSettings] = useState({ rules: [] });
  const [saleSearch, setSaleSearch] = useState("");
  const [salePage, setSalePage] = useState(1);
  const [salePeriod, setSalePeriod] = useState("month");
  const [saleMonth, setSaleMonth] = useState(new Date().toISOString().slice(0, 7));
  const [saleDates, setSaleDates] = useState({ from: "", to: "" });
  const [saleMeta, setSaleMeta] = useState({ total_count: 0, total_pages: 1, paid_total: 0, unpaid_total: 0, paid_count: 0, unpaid_count: 0 });
  const [productActionBusy, setProductActionBusy] = useState("");
  const [confirmFor, setConfirmFor] = useState(null);
  const [confirmForm, setConfirmForm] = useState({ customer_email: "", hold_minutes: "60" });

  const load = async () => {
    const saleParams = {
      page: salePage,
      size: 10,
      q: saleSearch,
      period: salePeriod,
      month: saleMonth,
      date_from: saleDates.from,
      date_to: saleDates.to,
    };
    const [inv, sl, st, rf, pb, bs] = await Promise.all([
      api.get("/inventory"), api.get("/shop/sales", { params: saleParams }), api.get("/shop/stats"),
      api.get("/shop/refunds").catch(() => ({ data: [] })),
      api.get("/admin/product-bookings").catch(() => ({ data: [] })),
      api.get("/billing/settings").catch(() => ({ data: { rules: [] } })),
    ]);
    setInventory(inv.data); setSales(sl.data.sales || sl.data || []); setSaleMeta(sl.data.sales ? sl.data : saleMeta); setStats(st.data); setRefunds(rf.data);
    setProductBookings(pb.data || []);
    setBillingSettings(bs.data || { rules: [] });
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [salePage, salePeriod, saleMonth, saleDates.from, saleDates.to]);
  useGarageWS(token, (e) => {
    const type = e.type || "";
    if (
      type === "NOTIFICATION_CREATED"
      || type === "SHOP_SALE"
      || type === "SHOP_PAYMENT_RECEIVED"
      || type.startsWith("PRODUCT_BOOKING_")
      || type.startsWith("REFUND_")
    ) {
      load();
      setTick((t) => t + 1);
      if (type === "SHOP_PAYMENT_RECEIVED") toast.success("Payment received! Invoice email is ready for the customer.");
      if (type === "NOTIFICATION_CREATED") toast(e.data?.event_type?.replace(/_/g, " ") || "Notification", { description: e.data?.body || "" });
    }
  });

  const categories = useMemo(() => {
    const set = new Set();
    inventory.forEach((i) => { if (i.category) set.add(i.category); });
    return ["all", ...Array.from(set).sort()];
  }, [inventory]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return inventory.filter((i) =>
      i.stock > 0
      && (category === "all" || (i.category || "") === category)
      && (i.name.toLowerCase().includes(q) || i.sku.toLowerCase().includes(q) || (i.category || "").toLowerCase().includes(q))
    );
  }, [inventory, search, category]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  useEffect(() => { setPage(1); }, [search, category]);

  const cartSubtotal = cart.reduce((s, c) => s + c.price * c.qty, 0);
  const cartFitting = parseFloat(fittingCharge) || 0;
  const manualAmount = parseFloat(manualAddOn.amount) || 0;
  const autoAddons = (billingSettings.rules || [])
    .filter((r) => r.active !== false && ["both", "shop"].includes(r.scope || "both"))
    .map((r) => ({ ...r, amount: r.type === "fixed" ? Number(r.rate || 0) : Math.round(cartSubtotal * Number(r.rate || 0)) / 100 }));
  const cartTotal = cartSubtotal + cartFitting + manualAmount + autoAddons.reduce((s, r) => s + Number(r.amount || 0), 0);
  const paymentHref = (link) => !link ? "" : (/^https?:\/\//i.test(link) ? link : `${window.location.origin}${link}`);

  const refundsBySale = useMemo(() => {
    const m = {};
    refunds.forEach((r) => { m[r.sale_id] = r; });
    return m;
  }, [refunds]);

  const filteredSales = sales;
  const saleTotalPages = Math.max(1, Number(saleMeta.total_pages || 1));
  const safeSalePage = Math.min(salePage, saleTotalPages);
  const pagedSales = filteredSales;
  const groupedSales = useMemo(() => {
    const groups = {};
    pagedSales.forEach((sale) => {
      const key = String(sale.created_at || sale.paid_at || "").slice(0, 10) || "Unknown";
      groups[key] = groups[key] || [];
      groups[key].push(sale);
    });
    return Object.entries(groups);
  }, [pagedSales]);
  const lockedPickups = useMemo(() => productBookings.filter((b) => b.stock_locked === true && b.status === "CONFIRMED"), [productBookings]);
  const pendingProductBookings = useMemo(() => productBookings.filter((b) => b.status === "REQUESTED"), [productBookings]);
  const isExpired = (booking) => booking.locked_until && new Date(booking.locked_until) < new Date();

  const addToCart = (it) => {
    setCart((prev) => {
      const found = prev.find((p) => p.id === it.id);
      if (found) {
        if (found.qty >= it.stock) { toast.error("No more stock"); return prev; }
        return prev.map((p) => p.id === it.id ? { ...p, qty: p.qty + 1 } : p);
      }
      return [...prev, { id: it.id, name: it.name, sku: it.sku, price: it.price, qty: 1, stock: it.stock }];
    });
  };
  const addLockedPickupToCart = (booking) => {
    const cartId = `booking-${booking.id}`;
    setCart((prev) => {
      if (prev.some((p) => p.id === cartId)) {
        toast.message("Locked pickup already in cart");
        return prev;
      }
      return [...prev, {
        id: cartId,
        inventory_id: booking.inventory_id,
        product_booking_id: booking.id,
        name: booking.product_name,
        sku: booking.sku,
        price: Number(booking.price || 0),
        qty: Number(booking.qty || 1),
        stock: Number(booking.qty || 1),
        lockedPickup: true,
      }];
    });
    setCustomer({ name: booking.customer_name || "", phone: booking.customer_phone || "", email: booking.customer_email || "" });
    toast.success("Locked pickup added to billing cart");
  };
  const decQty = (id) => setCart((prev) => prev.flatMap((p) => p.id === id ? (p.qty > 1 ? [{ ...p, qty: p.qty - 1 }] : []) : [p]));
  const removeFromCart = (id) => setCart((prev) => prev.filter((p) => p.id !== id));

  const checkout = async () => {
    if (cart.length === 0) return toast.error("Cart is empty");
    if (customer.phone && !isValidIndianPhone(customer.phone)) return toast.error("Enter a valid Indian mobile number");
    if (customer.email && !isValidEmail(customer.email)) return toast.error("Enter a valid customer email");
    try {
      const r = await api.post("/shop/sales", {
        customer_name: customer.name,
        customer_phone: customer.phone ? normalizeIndianPhone(customer.phone) : "",
        customer_email: customer.email.trim().toLowerCase(),
        items: cart.map((c) => ({ inventory_id: c.inventory_id || c.id, product_booking_id: c.product_booking_id, qty: c.qty })),
        payment_method: paymentMethod,
          fitting_charge: cartFitting,
          manual_lines: manualAmount ? [{ label: manualAddOn.label || "Manual add-on", amount: manualAmount }] : [],
      });
      toast.success(`Sale ₹${r.data.total} ${paymentMethod === "cash" ? "received" : "link sent"}`);
      setLastSale(r.data);
      if (paymentMethod === "razorpay" && r.data.payment_link) {
        try {
          await navigator.clipboard.writeText(paymentHref(r.data.payment_link));
          toast.success("Payment link copied");
        } catch {
          toast.message("Payment link ready", { description: r.data.payment_link });
        }
      }
      setCart([]); setCustomer({ name: "", phone: "", email: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Checkout failed"); }
  };

  const submitRefund = async () => {
    if (!refundFor) return;
    if (!refundReason.trim()) return toast.error("Please give a reason");
    try {
      await api.post("/shop/refunds", { sale_id: refundFor.id, reason: refundReason.trim() });
      toast.success("Refund request sent to admin for approval");
      setRefundFor(null); setRefundReason("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to raise refund"); }
  };

  const releaseProductBooking = async (booking) => {
    try {
      await api.post(`/admin/product-bookings/${booking.id}/release`);
      toast.success("Locked product released back to inventory");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not release product");
    }
  };

  const productBookingAction = async (booking, action, body) => {
    setProductActionBusy(`${booking.id}-${action}`);
    try {
      await api.post(`/admin/product-bookings/${booking.id}/${action}`, body);
      toast.success(action === "confirm" ? "Product locked and email sent" : "Product booking updated");
      if (action === "confirm") setConfirmFor(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Product booking action failed");
    } finally {
      setProductActionBusy("");
    }
  };

  const openProductConfirm = (booking) => {
    setConfirmFor(booking);
    setConfirmForm({ customer_email: booking.customer_email || "", hold_minutes: String(booking.hold_minutes || 60) });
  };

  const submitProductConfirm = (e) => {
    e.preventDefault();
    if (!confirmFor) return;
    if (!isValidEmail(confirmForm.customer_email)) return toast.error("Enter a valid customer email");
    productBookingAction(confirmFor, "confirm", {
      customer_email: confirmForm.customer_email.trim().toLowerCase(),
      hold_minutes: Number(confirmForm.hold_minutes || 60),
    });
  };

  const refundBadge = (status) => {
    if (!status) return null;
    const map = {
      PENDING: "bg-yellow-500/10 border-yellow-500/30 text-yellow-400",
      APPROVED: "bg-blue-500/10 border-blue-500/30 text-blue-400",
      REJECTED: "bg-zinc-700 text-zinc-400 border-zinc-600",
    };
    return <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border ${map[status] || ""}`}>Refund {status}</span>;
  };

  return (
    <div className="dark min-h-screen bg-zinc-950 text-zinc-100">
      <header className="sticky top-0 z-20 bg-zinc-950/90 backdrop-blur-xl border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <MacJitLogo size={32} />
            <div>
              <p className="font-display font-black text-lg tracking-tighter">MACJIT <span className="text-orange-500">/ SHOP</span></p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a href="/employee" data-testid="hr-link" className="border border-zinc-800 hover:border-orange-500 hover:text-orange-500 text-zinc-300 font-mono text-[10px] uppercase tracking-widest px-3 py-2 transition-colors">HR</a>
            <NotificationBell refreshKey={tick} />
            <button data-testid="logout-btn" onClick={logout} className="p-2 hover:bg-zinc-800 rounded-full"><LogOut className="w-4 h-4" /></button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-zinc-800">
          <StatCard testid="shop-stat-today" label="Today sales" value={stats.today_count || 0} accent />
          <StatCard testid="shop-stat-revenue" label="Today revenue" value={`₹${stats.today_revenue || 0}`} />
          <StatCard testid="shop-stat-pending" label="Unpaid" value={stats.pending_count || 0} sub="awaiting payment" />
          <StatCard testid="shop-stat-total" label="All-time" value={stats.total_sales || 0} sub="counter sales" />
        </div>

        {pendingProductBookings.length > 0 && (
          <section className="border border-zinc-800 bg-zinc-900/40 p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">New product bookings · {pendingProductBookings.length}</p>
              <ShoppingCart className="w-4 h-4 text-orange-500" />
            </div>
            <div className="grid md:grid-cols-2 gap-2">
              {pendingProductBookings.map((b) => {
                const total = Number(b.price || 0) * Number(b.qty || 1);
                return (
                  <div key={b.id} className="bg-zinc-950 border border-zinc-800 p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-display font-bold">{b.product_name} <span className="font-mono text-xs text-zinc-500">x{b.qty || 1}</span></p>
                        <p className="font-mono text-[10px] text-zinc-500">Incident #{String(b.id || "").slice(0, 8).toUpperCase()} · ₹{total}</p>
                        <p className="font-mono text-[10px] text-zinc-300 mt-1">{b.customer_name} · {b.customer_phone}</p>
                        {b.note && <p className="text-xs text-zinc-400 mt-2 italic">"{b.note}"</p>}
                      </div>
                      <span className="border border-yellow-500/30 bg-yellow-500/10 text-yellow-400 font-mono text-[9px] uppercase tracking-widest px-2 py-1">Requested</span>
                    </div>
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-zinc-800">
                      <a href={`tel:${b.customer_phone}`} className="font-mono text-[10px] uppercase tracking-widest text-orange-500 hover:text-orange-400">Call</a>
                      <a href={`https://wa.me/${(b.customer_phone || "").replace(/[^0-9]/g, "")}`} target="_blank" rel="noopener noreferrer" className="font-mono text-[10px] uppercase tracking-widest text-emerald-500 hover:text-emerald-400">WhatsApp</a>
                      <span className="flex-1" />
                      <button disabled={productActionBusy === `${b.id}-confirm`} onClick={() => openProductConfirm(b)} className="font-mono text-[10px] uppercase tracking-widest text-emerald-400 hover:text-emerald-300 disabled:opacity-50">Confirm + lock</button>
                      <button disabled={productActionBusy === `${b.id}-close`} onClick={() => productBookingAction(b, "close")} className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-zinc-300 disabled:opacity-50">Close</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {lockedPickups.length > 0 && (
          <section className="border border-zinc-800 bg-zinc-900/40 p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Locked product pickups · {lockedPickups.length}</p>
              <ShoppingCart className="w-4 h-4 text-orange-500" />
            </div>
            <div className="grid md:grid-cols-2 gap-2">
              {lockedPickups.map((b) => {
                const expired = isExpired(b);
                return (
                  <div key={b.id} className={`bg-zinc-950 border p-3 ${expired ? "border-red-500/40" : "border-zinc-800"}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-display font-bold">{b.product_name} <span className="font-mono text-xs text-zinc-500">x{b.qty || 1}</span></p>
                        <p className="font-mono text-[10px] text-zinc-500">Incident #{String(b.id || "").slice(0, 8).toUpperCase()} · {b.customer_name} · {b.customer_phone}</p>
                        <p className={`font-mono text-[10px] mt-1 ${expired ? "text-red-400" : "text-orange-500"}`}>
                          {expired ? "Expired: " : "Collect before: "}{b.locked_until ? new Date(b.locked_until).toLocaleString() : "No time set"}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button title="Add locked pickup to billing cart" onClick={() => addLockedPickupToCart(b)} className="bg-orange-500 hover:bg-orange-400 text-black p-2">
                          <Plus className="w-4 h-4" />
                        </button>
                        <button title="Release locked stock" onClick={() => releaseProductBooking(b)} className={`font-mono text-[10px] uppercase tracking-widest px-3 py-2 border ${expired ? "border-red-500/50 text-red-400 hover:bg-red-500/10" : "border-orange-500/50 text-orange-400 hover:bg-orange-500/10"}`}>
                          Release
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Catalog */}
          <div className="lg:col-span-2 border border-zinc-800 bg-zinc-900/40 p-6">
            <div className="flex items-center justify-between gap-3 mb-4">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Seller inventory</p>
                <p className="text-xs text-zinc-500 mt-1">Use approved inventory for counter billing and locked product pickups.</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 mb-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <input data-testid="shop-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search parts (SKU, name, category)..." className="w-full bg-zinc-950 border border-zinc-800 pl-10 pr-3 py-2.5 font-mono text-sm focus:border-orange-500 outline-none" />
              </div>
              <select
                data-testid="shop-category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="bg-zinc-950 border border-zinc-800 px-3 py-2.5 font-mono text-sm focus:border-orange-500 outline-none uppercase tracking-widest"
              >
                {categories.map((c) => (
                  <option key={c} value={c}>{c === "all" ? "All categories" : c}</option>
                ))}
              </select>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              {pageItems.map((i) => (
                <button key={i.id} data-testid={`shop-item-${i.id}`} onClick={() => addToCart(i)} className="text-left bg-zinc-950 border border-zinc-800 hover:border-orange-500 transition-colors p-3 flex items-center justify-between">
                  <div>
                    <p className="font-display font-bold text-sm">{i.name}</p>
                    <p className="font-mono text-[10px] text-zinc-500">{i.sku} · {i.category} · stock {i.stock}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-display font-black">₹{i.price}</span>
                    <span className="bg-orange-500 text-black p-1.5"><Plus className="w-3 h-3" /></span>
                  </div>
                </button>
              ))}
              {filtered.length === 0 && <p className="text-sm text-zinc-500 col-span-2">No matching items</p>}
            </div>

            {/* Pagination */}
            {filtered.length > PAGE_SIZE && (
              <div className="mt-4 flex items-center justify-between border-t border-zinc-800 pt-3" data-testid="shop-pagination">
                <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                  {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filtered.length)} of {filtered.length}
                </p>
                <div className="flex items-center gap-1">
                  <button data-testid="page-prev" disabled={safePage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="p-2 border border-zinc-800 hover:border-orange-500 hover:text-orange-500 disabled:opacity-30 disabled:cursor-not-allowed"><ChevronLeft className="w-3.5 h-3.5" /></button>
                  <span className="font-mono text-xs px-3">Page {safePage} / {totalPages}</span>
                  <button data-testid="page-next" disabled={safePage >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="p-2 border border-zinc-800 hover:border-orange-500 hover:text-orange-500 disabled:opacity-30 disabled:cursor-not-allowed"><ChevronRight className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            )}
          </div>

          {/* Cart */}
          <aside className="border border-zinc-800 bg-zinc-900/40 p-6 sticky top-24 self-start">
            <div className="flex items-center gap-2 mb-3">
              <ShoppingCart className="w-4 h-4 text-orange-500" />
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Cart · {cart.length}</p>
            </div>
            {cart.length === 0 ? (
              <p className="text-sm text-zinc-500 mb-4">Click items to add</p>
            ) : (
              <div className="space-y-2 mb-4 max-h-72 overflow-y-auto">
                {cart.map((c) => (
                  <div key={c.id} data-testid={`cart-item-${c.id}`} className="bg-zinc-950 border border-zinc-800 px-3 py-2 flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-bold truncate">{c.name}</p>
                      <p className="font-mono text-[10px] text-zinc-500">₹{c.price} × {c.qty}{c.lockedPickup ? " · locked pickup" : ""}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      {!c.lockedPickup && <button data-testid={`cart-dec-${c.id}`} onClick={() => decQty(c.id)} className="p-1 hover:bg-zinc-800"><Minus className="w-3 h-3" /></button>}
                      <span className="font-mono text-sm w-5 text-center">{c.qty}</span>
                      {!c.lockedPickup && <button data-testid={`cart-inc-${c.id}`} onClick={() => addToCart({ ...c, stock: c.stock })} className="p-1 hover:bg-zinc-800"><Plus className="w-3 h-3" /></button>}
                      <button data-testid={`cart-remove-${c.id}`} onClick={() => removeFromCart(c.id)} className="p-1 hover:bg-red-500/10 text-red-400"><X className="w-3 h-3" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="border-t border-zinc-800 pt-3 mb-3">
              <input data-testid="shop-customer-name" placeholder="Customer name (optional)" value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2 font-mono text-sm mb-2" />
              <input data-testid="shop-customer-phone" placeholder="Phone" value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} onBlur={() => customer.phone && setCustomer({ ...customer, phone: normalizeIndianPhone(customer.phone) })} className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2 font-mono text-sm mb-2" />
              <input data-testid="shop-customer-email" type="email" placeholder="Email for payment link" value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value.toLowerCase() })} className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2 font-mono text-sm" />
            </div>

            <div className="flex gap-2 mb-3">
              <button data-testid="pay-cash" onClick={() => setPaymentMethod("cash")} className={`flex-1 font-mono text-[10px] uppercase tracking-widest font-bold py-2 ${paymentMethod === "cash" ? "bg-orange-500 text-black" : "bg-zinc-950 border border-zinc-800 text-zinc-400"}`}>Cash</button>
              <button data-testid="pay-rzp" onClick={() => setPaymentMethod("razorpay")} className={`flex-1 font-mono text-[10px] uppercase tracking-widest font-bold py-2 ${paymentMethod === "razorpay" ? "bg-orange-500 text-black" : "bg-zinc-950 border border-zinc-800 text-zinc-400"}`}>Razorpay Link</button>
            </div>

            <div className="border border-zinc-800 bg-zinc-900/20 p-3 space-y-2 mb-3">
              <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Billing add-ons</p>
              <div className="flex gap-2">
                <div className="flex-1">
                  <label className="font-mono text-[10px] text-zinc-500 block mb-1">Fitting (₹)</label>
                  <input type="number" min="0" step="1" value={fittingCharge} onChange={(e) => setFittingCharge(e.target.value)} placeholder="0" className="w-full bg-zinc-950 border border-zinc-800 px-2 py-1.5 font-mono text-sm text-zinc-100 focus:border-orange-500 outline-none" />
                </div>
                <div className="flex-1">
                  <label className="font-mono text-[10px] text-zinc-500 block mb-1">Manual add-on ₹</label>
                  <input type="number" min="0" step="1" value={manualAddOn.amount} onChange={(e) => setManualAddOn((m) => ({ ...m, amount: e.target.value }))} placeholder="0" className="w-full bg-zinc-950 border border-zinc-800 px-2 py-1.5 font-mono text-sm text-zinc-100 focus:border-orange-500 outline-none" />
                </div>
              </div>
              <input value={manualAddOn.label} onChange={(e) => setManualAddOn((m) => ({ ...m, label: e.target.value }))} placeholder="Manual add-on label" className="w-full bg-zinc-950 border border-zinc-800 px-2 py-1.5 font-mono text-sm text-zinc-100 focus:border-orange-500 outline-none" />
              {autoAddons.map((a, i) => (
                <div key={`${a.key}-${i}`} className="flex justify-between font-mono text-[11px] text-orange-400">
                  <span>{a.label}</span><span>₹{a.amount}</span>
                </div>
              ))}
            </div>

            <div className="flex items-baseline justify-between mb-3">
              <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Total</span>
              <span className="font-display font-black text-3xl text-orange-500 flex items-center"><IndianRupee className="w-6 h-6" />{cartTotal}</span>
            </div>
            <button data-testid="checkout-btn" onClick={checkout} disabled={cart.length === 0} className="w-full bg-orange-500 hover:bg-orange-400 disabled:opacity-30 disabled:cursor-not-allowed text-black font-display font-black uppercase tracking-widest py-4 transition-colors">Checkout · {paymentMethod === "cash" ? "Collect Cash" : "Send Pay Link"}</button>

            {lastSale && (
              <div data-testid="last-sale" className="mt-4 bg-emerald-500/10 border border-emerald-500/30 p-3">
                <p className="font-mono text-[10px] uppercase tracking-widest text-emerald-400 flex items-center gap-1"><Receipt className="w-3 h-3" />Last Sale</p>
                <p className="font-display font-bold mt-1">#{lastSale.id.slice(0, 8)} · ₹{lastSale.total}</p>
                {lastSale.payment_link && <a href={paymentHref(lastSale.payment_link)} target="_blank" rel="noreferrer" className="text-xs text-orange-500 underline">Open payment link</a>}
              </div>
            )}
          </aside>
        </div>

        {/* Recent sales + refund actions */}
        <div className="border border-zinc-800 bg-zinc-900/40 p-6">
          <div className="flex items-start justify-between flex-wrap gap-4 mb-4">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">Counter sales history</p>
              <p className="font-display font-black text-3xl text-orange-500 mt-1">₹{Math.round(saleMeta.paid_total || 0).toLocaleString("en-IN")}</p>
              <p className="font-mono text-[10px] text-zinc-500">
                {saleMeta.total_count || 0} sales · {saleMeta.paid_count || 0} paid
                {Number(saleMeta.unpaid_total || 0) > 0 ? ` · unpaid ₹${Math.round(saleMeta.unpaid_total || 0).toLocaleString("en-IN")}` : ""}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 justify-end">
              <div className="flex bg-zinc-950 border border-zinc-800">
                {[["month", "Monthly"], ["custom", "Custom"], ["all", "All"]].map(([value, label]) => (
                  <button
                    key={value}
                    onClick={() => { setSalePeriod(value); setSalePage(1); }}
                    className={`px-3 py-2 font-mono text-[10px] uppercase tracking-widest font-bold ${salePeriod === value ? "bg-orange-500 text-black" : "text-zinc-400 hover:text-orange-500"}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {salePeriod === "month" && (
                <input
                  type="month"
                  value={saleMonth}
                  onChange={(e) => { setSaleMonth(e.target.value); setSalePage(1); }}
                  className="bg-zinc-950 border border-zinc-700 px-3 py-2 font-mono text-xs text-zinc-100 focus:border-orange-500 outline-none"
                />
              )}
              {salePeriod === "custom" && (
                <>
                  <input type="date" value={saleDates.from} onChange={(e) => { setSaleDates((d) => ({ ...d, from: e.target.value })); setSalePage(1); }} className="bg-zinc-950 border border-zinc-700 px-3 py-2 font-mono text-xs text-zinc-100 focus:border-orange-500 outline-none" />
                  <span className="font-mono text-xs text-zinc-500">to</span>
                  <input type="date" value={saleDates.to} onChange={(e) => { setSaleDates((d) => ({ ...d, to: e.target.value })); setSalePage(1); }} className="bg-zinc-950 border border-zinc-700 px-3 py-2 font-mono text-xs text-zinc-100 focus:border-orange-500 outline-none" />
                </>
              )}
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500 pointer-events-none" />
                <input
                  type="text"
                  value={saleSearch}
                  onChange={(e) => setSaleSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { setSalePage(1); load(); } }}
                  placeholder="Customer / phone / ID…"
                  className="bg-zinc-950 border border-zinc-700 pl-7 pr-8 py-2 font-mono text-xs text-zinc-100 focus:border-orange-500 outline-none w-56"
                />
                {saleSearch && (
                  <button onClick={() => { setSaleSearch(""); setSalePage(1); setTimeout(load, 0); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300">
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
              <button onClick={() => { setSalePage(1); load(); }} className="bg-orange-500 hover:bg-orange-400 text-black font-mono text-[10px] uppercase tracking-widest font-bold px-3 py-2">Search</button>
            </div>
          </div>
          <div className="space-y-4">
            {groupedSales.map(([day, rows]) => (
              <div key={day}>
                <div className="flex items-center justify-between mb-2">
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">{day === "Unknown" ? "Unknown date" : new Date(day).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</p>
                  <p className="font-mono text-[10px] text-zinc-500">₹{Math.round(rows.filter((s) => s.paid).reduce((sum, s) => sum + Number(s.total || 0), 0)).toLocaleString("en-IN")}</p>
                </div>
                <div className="space-y-2">
                  {rows.map((s) => {
                    const r = refundsBySale[s.id];
                    const status = r?.status || s.refund_status;
                    const daysSince = (Date.now() - new Date(s.created_at).getTime()) / (1000 * 60 * 60 * 24);
                    const canRefund = s.paid && !status && daysSince <= 5;
                    return (
                      <div key={s.id} className="bg-zinc-950 border border-zinc-800 p-3 flex items-center justify-between flex-wrap gap-2">
                        <div>
                          <p className="font-display font-bold">{s.customer_name} <span className="text-zinc-500 font-normal text-xs">· {(s.items || []).length} item(s) · #{s.id.slice(0,8)}</span></p>
                          <p className="font-mono text-[10px] text-zinc-500">{new Date(s.created_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })} · {s.payment_method}</p>
                        </div>
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="font-display font-bold">₹{s.total}</span>
                          <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 ${s.paid ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-400" : "bg-orange-600/10 border border-orange-600/30 text-orange-500"}`}>{s.paid ? "Paid" : "Unpaid"}</span>
                          {refundBadge(status)}
                          {!s.paid && <button data-testid={`mark-paid-${s.id}`} onClick={async () => { await api.post(`/shop/sales/${s.id}/pay`); load(); }} className="bg-emerald-500 hover:bg-emerald-400 text-black font-mono text-[10px] uppercase tracking-widest font-bold px-2 py-1">Mark Paid</button>}
                          {!s.paid && s.payment_link && <a href={paymentHref(s.payment_link)} target="_blank" rel="noreferrer" className="border border-orange-500/50 text-orange-400 font-mono text-[10px] uppercase tracking-widest font-bold px-2 py-1">Open Pay Link</a>}
                          {canRefund && (
                            <button
                              data-testid={`refund-${s.id}`}
                              onClick={() => { setRefundFor(s); setRefundReason(""); }}
                              className="border border-yellow-500/50 hover:bg-yellow-500/10 text-yellow-400 font-mono text-[10px] uppercase tracking-widest font-bold px-2 py-1 flex items-center gap-1"
                            ><Undo2 className="w-3 h-3" /> Refund</button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
            {filteredSales.length === 0 && (
              <p className="text-sm text-zinc-500">{saleSearch ? "No sales match your search." : "No counter sales yet."}</p>
            )}
          </div>
          {saleTotalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-zinc-800">
              <span className="font-mono text-[10px] text-zinc-500">Page {safeSalePage} of {saleTotalPages}</span>
              <div className="flex gap-1">
                <button onClick={() => setSalePage((p) => Math.max(1, p - 1))} disabled={safeSalePage === 1}
                  className="p-1.5 border border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500 disabled:opacity-30 disabled:cursor-not-allowed">
                  <ChevronLeft className="w-3.5 h-3.5" />
                </button>
                {Array.from({ length: saleTotalPages }, (_, i) => i + 1).map((n) => (
                  <button key={n} onClick={() => setSalePage(n)}
                    className={`px-2.5 py-1 font-mono text-[10px] border ${n === safeSalePage ? "bg-orange-500 border-orange-500 text-black font-bold" : "border-zinc-700 text-zinc-400 hover:border-zinc-500 hover:text-white"}`}>
                    {n}
                  </button>
                ))}
                <button onClick={() => setSalePage((p) => Math.min(saleTotalPages, p + 1))} disabled={safeSalePage === saleTotalPages}
                  className="p-1.5 border border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500 disabled:opacity-30 disabled:cursor-not-allowed">
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Refund dialog */}
      <Dialog open={!!refundFor} onOpenChange={(v) => { if (!v) { setRefundFor(null); setRefundReason(""); } }}>
        <DialogContent className="bg-zinc-950 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="font-display uppercase">Raise refund request</DialogTitle>
          </DialogHeader>
          {refundFor && (
            <div className="space-y-3">
              <div className="bg-zinc-900 border border-zinc-800 p-3">
                <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Sale</p>
                <p className="font-display font-bold">#{refundFor.id.slice(0, 8)} · ₹{refundFor.total}</p>
                <p className="font-mono text-[10px] text-zinc-500">{refundFor.customer_name} · {refundFor.items.length} item(s)</p>
                <ul className="mt-2 space-y-0.5 text-xs text-zinc-300">
                  {refundFor.items.map((it, idx) => (
                    <li key={idx}>· {it.name} × {it.qty} <span className="text-zinc-500">(₹{it.subtotal})</span></li>
                  ))}
                </ul>
              </div>
              <textarea
                data-testid="refund-reason"
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
                placeholder="Reason (e.g. customer returned defective part)"
                rows={3}
                className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 font-mono text-sm focus:border-orange-500 outline-none"
              />
              <p className="text-xs text-zinc-500">Admin must approve before stock is restored. The customer will see the refund once approved.</p>
              <div className="flex gap-2">
                <button onClick={() => setRefundFor(null)} className="flex-1 border border-zinc-700 text-zinc-300 font-mono text-xs uppercase tracking-widest py-2.5">Cancel</button>
                <button data-testid="refund-submit" onClick={submitRefund} className="flex-1 bg-yellow-500 hover:bg-yellow-400 text-black font-display font-black uppercase tracking-widest py-2.5">Send for approval</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={!!confirmFor} onOpenChange={(open) => !open && setConfirmFor(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800">
          <DialogHeader><DialogTitle className="font-display uppercase">Confirm product pickup</DialogTitle></DialogHeader>
          <form onSubmit={submitProductConfirm} className="space-y-3">
            <div className="bg-zinc-900 border border-zinc-800 p-3">
              <p className="font-display font-bold">{confirmFor?.product_name}</p>
              <p className="font-mono text-[11px] text-zinc-500">Incident #{String(confirmFor?.id || "").slice(0, 8).toUpperCase()} · {confirmFor?.customer_name} · x{confirmFor?.qty || 1}</p>
            </div>
            <input
              required
              type="email"
              placeholder="Customer email for pickup confirmation"
              value={confirmForm.customer_email}
              onChange={(e) => setConfirmForm((f) => ({ ...f, customer_email: e.target.value }))}
              className="w-full bg-zinc-900 border border-zinc-800 px-3 py-3 font-mono text-sm focus:border-orange-500 outline-none"
            />
            <select
              value={confirmForm.hold_minutes}
              onChange={(e) => setConfirmForm((f) => ({ ...f, hold_minutes: e.target.value }))}
              className="w-full bg-zinc-900 border border-zinc-800 px-3 py-3 font-mono text-sm focus:border-orange-500 outline-none"
            >
              <option value="60">Lock for 1 hour</option>
              <option value="180">Lock for 3 hours</option>
              <option value="360">Lock for 6 hours</option>
              <option value="720">Lock for 12 hours</option>
              <option value="1440">Lock for 1 day</option>
            </select>
            <p className="font-mono text-[10px] text-zinc-500">This reduces your inventory now and emails the customer to collect before the selected time.</p>
            <button disabled={productActionBusy === `${confirmFor?.id}-confirm`} className="w-full bg-orange-500 hover:bg-orange-400 disabled:opacity-50 text-black font-display font-black uppercase tracking-widest py-3">
              {productActionBusy === `${confirmFor?.id}-confirm` ? "Confirming..." : "Confirm, lock and send email"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AddShopInventoryDialog({ inventory = [], onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", sku: "", category: "", price: "", stock: "", low_stock_threshold: "5" });
  const [customSku, setCustomSku] = useState(false);
  const [customCategory, setCustomCategory] = useState(false);
  const skus = [...new Set(inventory.map((i) => i.sku).filter(Boolean))].sort();
  const categories = [...new Set(inventory.map((i) => i.category).filter(Boolean))].sort();
  const existingItem = form.sku ? inventory.find((i) => (i.sku || "").toUpperCase() === form.sku.toUpperCase()) : null;
  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const reset = () => {
    setForm({ name: "", sku: "", category: "", price: "", stock: "", low_stock_threshold: "5" });
    setCustomSku(false);
    setCustomCategory(false);
  };
  const selectSku = (sku) => {
    const item = inventory.find((i) => i.sku === sku);
    if (!item) return update("sku", sku);
    setForm({
      name: item.name || "",
      sku: item.sku || "",
      category: item.category || "",
      price: item.price ?? "",
      stock: "",
      low_stock_threshold: String(item.low_stock_threshold ?? 5),
    });
    setCustomSku(false);
    setCustomCategory(false);
  };
  const submit = async () => {
    if (!form.name.trim()) return toast.error("Product name is required");
    if (!form.stock && form.stock !== 0) return toast.error("Stock quantity is required");
    try {
      await api.post("/inventory", {
        ...form,
        price: Number(form.price || 0),
        stock: Number(form.stock || 0),
        low_stock_threshold: Number(form.low_stock_threshold || 5),
      });
      toast.success(existingItem ? "Stock updated and product visible globally" : "Product posted to global parts marketplace");
      onDone();
      reset();
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save product");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(value) => { setOpen(value); if (!value) reset(); }}>
      <button onClick={() => setOpen(true)} className="bg-orange-500 hover:bg-orange-400 text-black font-mono text-xs uppercase tracking-widest font-bold px-3 py-2 flex items-center gap-1 transition-colors">
        <Plus className="w-3 h-3" /> Post product
      </button>
      <DialogContent className="bg-zinc-950 border-zinc-800">
        <DialogHeader><DialogTitle className="font-display uppercase">Post marketplace product</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <input placeholder="Product name" value={form.name} onChange={(e) => update("name", e.target.value)} className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 font-mono text-sm" />
          <div className="flex gap-2">
            {customSku ? (
              <input placeholder="New SKU" value={form.sku} onChange={(e) => update("sku", e.target.value)} className="min-w-0 flex-1 bg-zinc-900 border border-zinc-800 px-3 py-2 font-mono text-sm" />
            ) : (
              <select value={form.sku} onChange={(e) => selectSku(e.target.value)} className="min-w-0 flex-1 bg-zinc-900 border border-zinc-800 px-3 py-2 font-mono text-sm text-zinc-100">
                <option value="">Select SKU or add new</option>
                {skus.map((sku) => <option key={sku} value={sku}>{sku}</option>)}
              </select>
            )}
            <button type="button" onClick={() => { setCustomSku(true); setForm((f) => ({ ...f, sku: "", low_stock_threshold: "5" })); }} className="grid h-10 w-10 place-items-center border border-zinc-800 bg-zinc-900 text-orange-400 hover:border-orange-500" aria-label="Add new SKU">
              <Plus className="h-4 w-4" />
            </button>
          </div>
          <div className="flex gap-2">
            {customCategory ? (
              <input placeholder="New category" value={form.category} onChange={(e) => update("category", e.target.value)} className="min-w-0 flex-1 bg-zinc-900 border border-zinc-800 px-3 py-2 font-mono text-sm" />
            ) : (
              <select value={form.category} onChange={(e) => update("category", e.target.value)} className="min-w-0 flex-1 bg-zinc-900 border border-zinc-800 px-3 py-2 font-mono text-sm text-zinc-100">
                <option value="">Select category</option>
                {categories.map((category) => <option key={category} value={category}>{category}</option>)}
              </select>
            )}
            <button type="button" onClick={() => { setCustomCategory(true); update("category", ""); }} className="grid h-10 w-10 place-items-center border border-zinc-800 bg-zinc-900 text-orange-400 hover:border-orange-500" aria-label="Add new category">
              <Plus className="h-4 w-4" />
            </button>
          </div>
          <input placeholder="Price (₹)" type="number" min="0" value={form.price} onChange={(e) => update("price", e.target.value)} className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 font-mono text-sm" />
          <input placeholder={existingItem ? "Stock qty to add" : "Stock qty"} type="number" min="0" value={form.stock} onChange={(e) => update("stock", e.target.value)} className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 font-mono text-sm" />
          <input placeholder="Low stock threshold" type="number" min="0" value={form.low_stock_threshold} disabled={Boolean(existingItem)} onChange={(e) => update("low_stock_threshold", e.target.value)} className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 font-mono text-sm disabled:cursor-not-allowed disabled:text-zinc-500" />
          <p className="font-mono text-[10px] text-zinc-500">Stock belongs to your tenant and branch. Customers see it in the global parts page and bookings come back here.</p>
          <button onClick={submit} className="w-full bg-orange-500 hover:bg-orange-400 text-black font-display font-black uppercase tracking-widest py-3">{existingItem ? "Update stock" : "Post product"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
